import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Sensors, Warning, CheckCircle, Edit as EditIcon, Delete as DeleteIcon, PowerSettingsNew } from '@mui/icons-material';
import ReportsButton from '../../components/ReportsButton';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { barriersAPI, usersAPI, devicesAPI } from '../../services/api';
import toast from 'react-hot-toast';

function Barriers() {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const [barriers, setBarriers] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [availableGateways, setAvailableGateways] = useState([]);
  const [availableRfidReaders, setAvailableRfidReaders] = useState([]);
  const [open, setOpen] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [editingBarrier, setEditingBarrier] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    device_id: '',
    rssi_threshold: -60,
    detection_radius: 2.0,
    detection_type: 'ble',
    rfid_reader_id: '',
    confidence_threshold: 0.70,
    description: '',
    notification_recipients: []
  });

  const loadGateways = useCallback(async () => {
    try {
      const response = await devicesAPI.getAll();
      const devicesData = Array.isArray(response?.data?.data) ? response.data.data : 
                         Array.isArray(response?.data) ? response.data : [];
      const gateways = devicesData.filter(device => device.type === 'gateway' && device.status === 'online');
      const rfidReaders = devicesData.filter(device => device.type === 'rfid_reader' && device.status === 'online');
      setAvailableGateways(gateways);
      setAvailableRfidReaders(rfidReaders);
    } catch (error) {
      const mockGateways = [
        { id: 'gw-001', name: 'Gateway UTI', device_id: 'gateway_001', location: 'UTI - Porta Principal' },
        { id: 'gw-002', name: 'Gateway Emergência', device_id: 'gateway_002', location: 'Emergência - Porta Sul' },
        { id: 'gw-003', name: 'Gateway Centro Cirúrgico', device_id: 'gateway_003', location: 'CC - Entrada Principal' }
      ];
      const mockRfidReaders = [
        { id: 'rfid-001', name: 'Reader UTI', device_id: 'rfid_reader_001', location: 'UTI - Porta Principal' },
        { id: 'rfid-002', name: 'Reader Emergência', device_id: 'rfid_reader_002', location: 'Emergência - Porta Sul' }
      ];
      setAvailableGateways(mockGateways);
      setAvailableRfidReaders(mockRfidReaders);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const response = await usersAPI.getAll();
      const usersData = Array.isArray(response?.data?.data) ? response.data.data : 
                       Array.isArray(response?.data) ? response.data : [];
      setAvailableUsers(usersData.filter(user => user.active));
    } catch (error) {
      const currentTenant = tenantId;
      const mockUsers = [
        { id: '1', name: 'Dr. Silva', email: 'silva@hospital.com', role: 'admin', tenant_id: 'hospital-demo-001' },
        { id: '2', name: 'Enf. Maria', email: 'maria@hospital.com', role: 'manager', tenant_id: 'hospital-demo-001' },
        { id: '3', name: 'Téc. João', email: 'joao@hospital.com', role: 'technician', tenant_id: 'hospital-demo-001' },
        { id: '4', name: 'Dr. Santos', email: 'santos@hospital.com', role: 'admin', tenant_id: 'hospital-demo-001' },
        { id: '5', name: 'Dr. Einstein', email: 'einstein@einstein.com', role: 'admin', tenant_id: 'hospital-einstein' }
      ].filter(user => user.tenant_id === currentTenant && user.id !== '4');
      setAvailableUsers(mockUsers);
    }
  }, [tenantId]);

  const loadRecentEvents = useCallback(async () => {
    try {
      const response = await barriersAPI.getEvents();
      const eventsData = Array.isArray(response?.data?.data) ? response.data.data : 
                        Array.isArray(response?.data) ? response.data : [];
      setRecentEvents(eventsData);
    } catch (error) {
      const mockEvents = [
        {
          id: '1',
          barrier_name: 'Entrada UTI',
          equipment_name: 'Desfibrilador DEA-001',
          timestamp: '2024-08-17T20:45:00Z',
          status: 'expired',
          category: 'critical',
          alert_sent: true
        },
        {
          id: '2',
          barrier_name: 'Saída Emergência',
          equipment_name: 'Monitor Cardíaco MC-200',
          timestamp: '2024-08-17T20:30:00Z',
          status: 'expiring',
          category: 'critical',
          alert_sent: true
        },
        {
          id: '3',
          barrier_name: 'Entrada UTI',
          equipment_name: 'Bomba de Infusão BI-150',
          timestamp: '2024-08-17T20:15:00Z',
          status: 'valid',
          category: 'normal',
          alert_sent: false
        }
      ];
      setRecentEvents(mockEvents);
      console.warn('Barriers events API failed, using mock data:', error.message);
    }
  }, []);

  const loadBarriers = useCallback(async () => {
    try {
      const response = await barriersAPI.getAll();
      const barriersData = Array.isArray(response?.data?.data) ? response.data.data : 
                          Array.isArray(response?.data) ? response.data : [];
      setBarriers(barriersData);
    } catch (error) {
      const currentTenant = tenantId;
      const mockBarriers = [
        {
          id: '1',
          name: 'Entrada UTI',
          device_id: 'gateway_001',
          location: 'UTI - Porta Principal',
          rssi_threshold: -60,
          detection_radius: 2.0,
          status: 'active',
          last_detection: '2024-08-17T20:45:00Z',
          total_detections: 127,
          tenant_id: 'hospital-demo-001'
        },
        {
          id: '2',
          name: 'Saída Emergência',
          device_id: 'gateway_002',
          location: 'Emergência - Porta Sul',
          rssi_threshold: -55,
          detection_radius: 1.5,
          status: 'active',
          last_detection: '2024-08-17T20:30:00Z',
          total_detections: 89,
          tenant_id: 'hospital-demo-001'
        },
        {
          id: '3',
          name: 'Centro Cirúrgico',
          device_id: 'gateway_003',
          location: 'CC - Entrada Principal',
          rssi_threshold: -65,
          detection_radius: 2.5,
          status: 'inactive',
          last_detection: '2024-08-17T18:15:00Z',
          total_detections: 45,
          tenant_id: 'hospital-demo-001'
        }
      ].filter(barrier => barrier.tenant_id === currentTenant);
      setBarriers(mockBarriers);
    }
  }, [tenantId]);

  useEffect(() => {
    loadBarriers();
    loadRecentEvents();
    loadGateways();
    loadUsers();
  }, [loadBarriers, loadRecentEvents, loadGateways, loadUsers]);

  const handleEdit = (barrier) => {
    setEditingBarrier(barrier);
    setFormData({
      name: barrier.name,
      device_id: barrier.device_id,
      rssi_threshold: barrier.rssi_threshold,
      detection_radius: barrier.detection_radius,
      detection_type: barrier.detection_type || 'ble',
      rfid_reader_id: barrier.rfid_reader_id || '',
      confidence_threshold: barrier.confidence_threshold || 0.70,
      description: barrier.description || '',
      notification_recipients: barrier.notification_recipients || []
    });
    setOpen(true);
  };

  const handleToggleStatus = async (barrier) => {
    const currentActive = barrier.is_active !== undefined ? barrier.is_active : barrier.status === 'active';
    const newActive = !currentActive;
    const action = newActive ? 'ativar' : 'desativar';
    
    if (window.confirm(`Tem certeza que deseja ${action} a barreira "${barrier.name}"?`)) {
      try {
        await barriersAPI.update(barrier.id, { ...barrier, is_active: newActive });
        toast.success(`Barreira ${newActive ? 'ativada' : 'desativada'} com sucesso!`);
        await loadBarriers();
      } catch (error) {
        toast.error(error.response?.data?.message || `Erro ao ${action} barreira`);
      }
    }
  };

  const handleDelete = async (barrierId) => {
    if (window.confirm('Tem certeza que deseja excluir esta barreira?')) {
      try {
        await barriersAPI.delete(barrierId);
        toast.success('Barreira excluída com sucesso!');
        // Recarrega dados para atualizar tabela e cards
        await Promise.all([loadBarriers(), loadRecentEvents()]);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Erro ao excluir barreira');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingBarrier(null);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const barrierData = formData;
      
      if (editingBarrier) {
        await barriersAPI.update(editingBarrier.id, barrierData);
        toast.success('Barreira atualizada com sucesso!');
      } else {
        await barriersAPI.create(barrierData);
        toast.success('Barreira criada com sucesso!');
      }
      handleCloseDialog();
      // Recarrega dados para atualizar tabela e cards
      await Promise.all([loadBarriers(), loadRecentEvents()]);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar barreira');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      device_id: '',
      rssi_threshold: -60,
      detection_radius: 2.0,
      detection_type: 'ble',
      rfid_reader_id: '',
      confidence_threshold: 0.70,
      description: '',
      notification_recipients: []
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const toggleRecipient = (user) => {
    const isSelected = formData.notification_recipients.some(r => r.userId === user.id);
    
    if (isSelected) {
      setFormData({
        ...formData,
        notification_recipients: formData.notification_recipients.filter(r => r.userId !== user.id)
      });
    } else {
      setFormData({
        ...formData,
        notification_recipients: [...formData.notification_recipients, {
          userId: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
          alertTypes: ['always'],
          active: true
        }]
      });
    }
  };

  const getRecipientAlertTypes = (userId) => {
    const recipient = formData.notification_recipients.find(r => r.userId === userId);
    return recipient ? recipient.alertTypes : [];
  };

  const updateRecipientAlertTypes = (userId, alertTypes) => {
    setFormData({
      ...formData,
      notification_recipients: formData.notification_recipients.map(r => 
        r.userId === userId ? { ...r, alertTypes } : r
      )
    });
  };

  const getStatusColor = (barrier) => {
    const isActive = barrier.is_active !== undefined ? barrier.is_active : barrier.status === 'active';
    return isActive ? 'success' : 'error';
  };

  const getEventStatusColor = (status) => {
    switch (status) {
      case 'valid': return 'success';
      case 'expiring': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('barriers.title')}
        </Typography>
        <Box display="flex" gap={1}>
          <ReportsButton variant="outlined" size="medium" />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
          >
            {t('barriers.newBarrier')}
          </Button>
        </Box>
      </Box>

      {/* Alertas Ativos */}
      {recentEvents.filter(e => e.status === 'expired').length > 0 && (
        <Box mb={3}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            🚨 {recentEvents.filter(e => e.status === 'expired').length} equipamentos vencidos detectados nas últimas 2 horas
          </Alert>
        </Box>
      )}

      {/* Estatísticas */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Barreiras Ativas
              </Typography>
              <Typography variant="h4">
                {barriers.filter(b => b.is_active || b.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Detecções Hoje
              </Typography>
              <Typography variant="h4">
                {barriers.reduce((sum, b) => {
                  const detections = b.total_detections;
                  return sum + (typeof detections === 'number' && !isNaN(detections) ? detections : 0);
                }, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Alertas Enviados
              </Typography>
              <Typography variant="h4">
                {recentEvents.filter(e => e.alert_sent).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Equipamentos Vencidos
              </Typography>
              <Typography variant="h4" color="error">
                {recentEvents.filter(e => e.status === 'expired').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Barreiras */}
      <Paper sx={{ mb: 3 }}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Barreiras Configuradas
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Localização</TableCell>
                <TableCell>Tipo Detecção</TableCell>
                <TableCell>Device ID</TableCell>
                <TableCell>RSSI Threshold</TableCell>
                <TableCell>Raio (m)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Última Detecção</TableCell>
                <TableCell>Total Detecções</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(barriers) && barriers.map((barrier) => (
                <TableRow key={barrier.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Sensors sx={{ mr: 1 }} />
                      {barrier.name}
                    </Box>
                  </TableCell>
                  <TableCell>{barrier.device_location || barrier.location || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={barrier.detection_type === 'hybrid' ? 'BLE+RFID' : barrier.detection_type?.toUpperCase() || 'BLE'} 
                      color={barrier.detection_type === 'hybrid' ? 'secondary' : 'primary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{barrier.device_name || barrier.device_id}</TableCell>
                  <TableCell>{barrier.rssi_threshold} dBm</TableCell>
                  <TableCell>{barrier.detection_radius} m</TableCell>
                  <TableCell>
                    <Chip
                      label={barrier.is_active || barrier.status === 'active' ? t('common.active') : t('common.inactive')}
                      color={getStatusColor(barrier)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(barrier.last_detection)}</TableCell>
                  <TableCell>{barrier.total_detections}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<PowerSettingsNew />}
                      onClick={() => handleToggleStatus(barrier)}
                      color={(barrier.is_active || barrier.status === 'active') ? 'warning' : 'success'}
                      sx={{ mr: 1 }}
                    >
                      {(barrier.is_active || barrier.status === 'active') ? 'Desativar' : 'Ativar'}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(barrier)}
                      sx={{ mr: 1 }}
                    >
                      Editar
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(barrier.id)}
                    >
                      Excluir
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Eventos Recentes */}
      <Paper>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Eventos Recentes
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Barreira</TableCell>
                <TableCell>Equipamento</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Alerta Enviado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(recentEvents) && recentEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{formatDateTime(event.timestamp)}</TableCell>
                  <TableCell>{event.barrier_name}</TableCell>
                  <TableCell>{event.equipment_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(`common.${event.status}`)}
                      color={getEventStatusColor(event.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`equipment.${event.category}`)}
                      color={event.category === 'critical' ? 'error' : 'primary'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {event.alert_sent ? (
                      <CheckCircle color="success" />
                    ) : (
                      <Warning color="warning" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog para Nova Barreira */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBarrier ? 'Editar Barreira' : 'Nova Barreira'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Nome da Barreira"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de Detecção</InputLabel>
                  <Select
                    name="detection_type"
                    value={formData.detection_type}
                    onChange={handleChange}
                  >
                    <MenuItem value="ble">BLE Apenas</MenuItem>
                    <MenuItem value="rfid">RFID Apenas</MenuItem>
                    <MenuItem value="hybrid">Híbrido (BLE + RFID)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="confidence_threshold"
                  label="Limite de Confiança"
                  type="number"
                  step="0.01"
                  fullWidth
                  value={formData.confidence_threshold}
                  onChange={handleChange}
                  helperText="Score mínimo (0.0-1.0)"
                  inputProps={{ min: 0, max: 1, step: 0.01 }}
                />
              </Grid>
              {['ble', 'hybrid'].includes(formData.detection_type) && (
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Gateway BLE</InputLabel>
                    <Select
                      name="device_id"
                      value={formData.device_id}
                      onChange={handleChange}
                    >
                      {availableGateways.map((gateway) => (
                        <MenuItem key={gateway.id} value={gateway.id}>
                          {gateway.name} - {gateway.location}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  name="rssi_threshold"
                  label="RSSI Threshold (dBm)"
                  type="number"
                  fullWidth
                  value={formData.rssi_threshold}
                  onChange={handleChange}
                  helperText="Valor RSSI para detecção (-30 a -90)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="detection_radius"
                  label="Raio de Detecção (m)"
                  type="number"
                  step="0.1"
                  fullWidth
                  value={formData.detection_radius}
                  onChange={handleChange}
                  helperText="Raio em metros"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Descrição"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Destinatários de Notificação
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setRecipientsOpen(true)}
                  fullWidth
                >
                  Configurar Destinatários ({formData.notification_recipients.length})
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingBarrier ? 'Atualizar' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Recipients */}
      <Dialog open={recipientsOpen} onClose={() => setRecipientsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Configurar Destinatários de Notificação</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {Array.isArray(availableUsers) && availableUsers.map((user) => {
              const isSelected = formData.notification_recipients.some(r => r.userId === user.id);
              return (
                <Grid item xs={12} sm={6} key={user.id}>
                  <Paper sx={{ p: 2, border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle1">{user.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {user.email} - {user.role}
                        </Typography>
                      </Box>
                      <Button
                        variant={isSelected ? 'contained' : 'outlined'}
                        color={isSelected ? 'error' : 'primary'}
                        size="small"
                        onClick={() => toggleRecipient(user)}
                      >
                        {isSelected ? 'Remover' : 'Adicionar'}
                      </Button>
                    </Box>
                    {isSelected && (
                      <Box mt={2}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Tipos de Alerta</InputLabel>
                          <Select
                            multiple
                            value={getRecipientAlertTypes(user.id)}
                            onChange={(e) => updateRecipientAlertTypes(user.id, e.target.value)}
                          >
                            <MenuItem value="always">Sempre</MenuItem>
                            <MenuItem value="expired_only">Apenas Vencido</MenuItem>
                            <MenuItem value="critical_only">Apenas Crítico</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipientsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Barriers;